import { useEffect, useState } from "react";
import { staffApi, leadsApi, ApiError } from "@/api";
import type { QuoteTemplate, Quote } from "@/types";
import { Button, Spinner } from "@/components/ui";

/**
 * Create-quote template picker (§5.4). Lists active templates, pre-selects the
 * default (is_default), and requires a selection. On confirm it bridges via
 * POST /lead/{id}/create-quote and hands the new quote back to the caller. If
 * the lead is already linked (400), defers to onAlreadyLinked.
 */
export function TemplatePickerModal({
  leadId,
  shortDescription,
  onCreated,
  onAlreadyLinked,
  onClose,
  onError,
}: {
  leadId: number;
  /** Staff-typed "Description for quote" from the drawer — becomes the quote's
   *  short description (`project_description`). The drawer gates on it being
   *  non-empty before this modal can open. */
  shortDescription: string;
  onCreated: (quote: Quote) => void;
  onAlreadyLinked: () => void;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    staffApi
      .listTemplates()
      .then((list) => {
        if (cancelled) return;
        const active = list.filter((t) => t.is_active);
        setTemplates(active);
        setSelectedId(active.find((t) => t.is_default)?.id ?? null);
      })
      .catch((err) => {
        if (!cancelled) onError(err instanceof ApiError ? err.message : "Could not load templates.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [onError]);

  async function confirm() {
    if (selectedId == null) return;
    setCreating(true);
    try {
      const quote = await leadsApi.createQuoteFromLead(leadId, selectedId, shortDescription);
      onCreated(quote);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && /already linked/i.test(err.message)) {
        onAlreadyLinked();
      } else {
        onError(err instanceof ApiError ? err.message : "Could not create the quote.");
      }
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="picker__overlay" onClick={onClose}>
      <div className="picker" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Choose a template">
        <header className="picker__head">
          <h3>Create quote from template</h3>
          <button type="button" className="lead-drawer__close" onClick={onClose} aria-label="Close">✕</button>
        </header>

        {loading ? (
          <div className="picker__loading"><Spinner /></div>
        ) : templates.length === 0 ? (
          <p className="picker__empty">No active templates. Create one in Templates first.</p>
        ) : (
          <ul className="picker__list">
            {templates.map((t) => (
              <li key={t.id}>
                <label className={`picker__item${selectedId === t.id ? " is-selected" : ""}`}>
                  <input
                    type="radio"
                    name="template"
                    checked={selectedId === t.id}
                    onChange={() => setSelectedId(t.id)}
                  />
                  <span className="picker__item-body">
                    <span className="picker__item-name">
                      {t.name}
                      {t.is_default ? <span className="picker__default-tag">Default</span> : null}
                    </span>
                    {t.description ? <span className="picker__item-desc">{t.description}</span> : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        <footer className="picker__foot">
          <Button variant="ghost" onClick={onClose} disabled={creating}>Cancel</Button>
          <Button variant="primary" onClick={confirm} loading={creating} disabled={selectedId == null}>
            Create quote
          </Button>
        </footer>
      </div>
    </div>
  );
}
